import { useState } from "react";
import { MapPin, Navigation, Plus } from "lucide-react";
import { CLASS_LOCATIONS, CLASS_TIMEZONE, directionsUrl } from "../../data/classLocations";

const days = (schedule) => [...new Set(schedule.map((s) => s.day))].join(" · ");

const LocationCard = ({ loc, n, open, onToggle }) => {
  const headId = `loc-head-${loc.id}`;
  const panelId = `loc-panel-${loc.id}`;
  return (
    <li
      data-testid={`venue-${n}`}
      data-open={open}
      className={`rounded-[1.25rem] border transition-[background-color,border-color,box-shadow] duration-500 ${
        open ? "border-ink/10 bg-ivory-50 shadow-[0_24px_50px_-34px_rgba(43,38,32,0.45)]" : "border-transparent hover:bg-ivory-50/60"
      }`}
    >
      <h3>
        <button
          id={headId}
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={panelId}
          data-testid={`venue-toggle-${loc.id}`}
          className="group flex min-h-[72px] w-full items-center gap-4 rounded-[1.25rem] px-4 py-4 text-left sm:px-5"
        >
          <span className="w-6 shrink-0 text-xs tabular-nums text-coral-ink">0{n}</span>
          <span className="min-w-0 flex-1">
            <span className="block font-display text-2xl leading-tight text-ink transition-transform duration-500 group-hover:translate-x-1 sm:text-[1.7rem]">{loc.name}</span>
            <span className="mt-1 block text-xs font-medium tracking-wide text-ink-soft">
              {loc.detail} · {days(loc.schedule)}
            </span>
          </span>
          <span
            aria-hidden
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-[transform,background-color,border-color,color] duration-500 ${
              open ? "rotate-45 border-ink bg-ink text-ivory" : "border-ink/15 text-ink group-hover:border-ink"
            }`}
          >
            <Plus className="h-4 w-4" strokeWidth={1.5} />
          </span>
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={headId}
        inert={!open}
        data-testid={`venue-panel-${loc.id}`}
        className={`grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="overflow-hidden">
          <div className="grid gap-6 px-4 pb-6 sm:grid-cols-[1.2fr_1fr] sm:gap-8 sm:px-5 sm:pl-[3.75rem]">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-ink-soft">
                Schedule <span className="text-coral-ink">· Times {CLASS_TIMEZONE}</span>
              </p>
              <ul className="mt-3 divide-y divide-ink/10 border-y border-ink/10" data-testid={`venue-schedule-${loc.id}`}>
                {loc.schedule.map((s) => (
                  <li key={`${s.day}-${s.time}`} className="flex items-baseline gap-3 py-2.5 text-[15px]">
                    <span className="w-9 shrink-0 font-semibold text-ink">{s.day}</span>
                    <span className="w-[4.75rem] shrink-0 tabular-nums text-ink">{s.time}</span>
                    <span className="text-ink-soft">{s.className}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-ink-soft">Address</p>
              <p className="mt-3 flex gap-2 text-[15px] leading-relaxed text-ink" data-testid={`venue-address-${loc.id}`}>
                <MapPin aria-hidden className="mt-1 h-4 w-4 shrink-0 text-coral-ink" strokeWidth={1.5} />
                {loc.address}
              </p>
              <a
                href={directionsUrl(loc.address)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`venue-directions-${loc.id}`}
                className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-full border border-ink/25 px-5 text-sm font-semibold text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-ivory"
              >
                <Navigation aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                Get Directions<span className="sr-only"> to {loc.name} (opens in a new tab)</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

export default function LocationAccordion() {
  const [openId, setOpenId] = useState(null);
  return (
    <ul className="mt-4 space-y-1.5" data-testid="classes-venues">
      {CLASS_LOCATIONS.map((loc, i) => (
        <LocationCard key={loc.id} loc={loc} n={i + 1} open={openId === loc.id} onToggle={() => setOpenId((o) => (o === loc.id ? null : loc.id))} />
      ))}
    </ul>
  );
}
